/** Helpers for the moderation pages (hub /api/moderation). Pure, no imports — tests/moderationView.test.ts loads it directly. */

export type ModMode = 'off' | 'shadow' | 'live';
export type ModAction = 'hide' | 'delete' | 'flag' | 'none';
export interface PolicyFields {
  mode: ModMode;
  abuseAction: ModAction;
  spamAction: ModAction;
  complaintAction: ModAction;
  notifyOperator: boolean;
}
export interface CategoryGate {
  precision: number;
  recall: number;
  support: number;
  live: boolean;
}
export interface EvalSummary {
  id: string;
  at: string;
  n: number;
  perCategory: Record<string, CategoryGate>;
}

export const GATE = { minPrecision: 0.95, minSupport: 200 };

export const MODE_TEXT: Record<ModMode, string> = { off: 'Off', shadow: 'Watch only', live: 'Live' };
export const MODE_HELP: Record<ModMode, string> = {
  off: 'Comments are not checked.',
  shadow: 'Comments are checked and recorded; nothing happens to them.',
  live: 'Comments are hidden, deleted or flagged as set below.',
};
export const ACTION_TEXT: Record<ModAction, string> = {
  hide: 'Hide',
  delete: 'Delete',
  flag: 'Flag for operator',
  none: 'Do nothing',
};
export const ACTIONS_FOR: { abuse: ModAction[]; spam: ModAction[]; complaint: ModAction[] } = {
  abuse: ['hide', 'delete', 'flag', 'none'],
  spam: ['hide', 'delete', 'flag', 'none'],
  complaint: ['flag', 'none'],
};
export const CATEGORY_TEXT: Record<string, string> = {
  abuse: 'Abuse',
  spam: 'Spam',
  complaint: 'Complaint',
  none: 'Normal',
};

/** Same rule as backend moderation/policy.ts liveBlockers — the server enforces it; this shows the reason before saving. */
export function liveBlockers(p: PolicyFields, run: EvalSummary | null): string[] {
  if (p.mode !== 'live') return [];
  const acting = (['abuse', 'spam'] as const).filter((c) => {
    const a = c === 'abuse' ? p.abuseAction : p.spamAction;
    return a === 'hide' || a === 'delete';
  });
  if (!acting.length) return [];
  if (!run) return ['no evaluation has been run yet (Platform → Moderation → Evaluation)'];
  return acting
    .filter((c) => !run.perCategory[c]?.live)
    .map((c) => {
      const g = run.perCategory[c];
      const now = g ? `${Math.round(g.precision * 100)}% precise on ${g.support} labels` : 'not evaluated';
      return `${c}: ${now} — needs ≥${GATE.minPrecision * 100}% on ≥${GATE.minSupport} labels`;
    });
}

/** Owner decision 2026-10-07 (option A): an already-live bot is not switched back automatically — it is warned. */
export function liveWarning(p: PolicyFields, run: EvalSummary | null): string | null {
  const b = liveBlockers(p, run);
  return b.length ? `The latest evaluation no longer supports live — ${b.join('; ')}. Switch to Watch only or fix detection.` : null;
}

export function policyChanges(a: PolicyFields, b: PolicyFields): string[] {
  const out: string[] = [];
  if (a.mode !== b.mode) out.push(`Mode: ${MODE_TEXT[a.mode]} → ${MODE_TEXT[b.mode]}`);
  if (a.abuseAction !== b.abuseAction) out.push(`Abuse: ${ACTION_TEXT[a.abuseAction]} → ${ACTION_TEXT[b.abuseAction]}`);
  if (a.spamAction !== b.spamAction) out.push(`Spam: ${ACTION_TEXT[a.spamAction]} → ${ACTION_TEXT[b.spamAction]}`);
  if (a.complaintAction !== b.complaintAction) {
    out.push(`Complaint: ${ACTION_TEXT[a.complaintAction]} → ${ACTION_TEXT[b.complaintAction]}`);
  }
  if (a.notifyOperator !== b.notifyOperator) {
    out.push(`Notify operator: ${a.notifyOperator ? 'on' : 'off'} → ${b.notifyOperator ? 'on' : 'off'}`);
  }
  return out;
}

const pct = (x: number) => `${Math.round(x * 100)}%`;

export function gateRows(run: EvalSummary | null) {
  if (!run) return [];
  return ['abuse', 'spam', 'complaint'].map((category) => {
    const g = run.perCategory[category] ?? { precision: 0, recall: 0, support: 0, live: false };
    const missing = Math.max(GATE.minSupport - g.support, 0);
    const note = g.live
      ? 'ready'
      : missing > 0
        ? `needs ${missing} more labels`
        : `precision below ${GATE.minPrecision * 100}%`;
    return { category, precision: pct(g.precision), recall: pct(g.recall), support: g.support, ready: g.live, note };
  });
}

export interface HealthLike {
  checks1h: number;
  lastAt: string | null;
  failed24h: number;
  stuckPending: number;
}

/** One status per caller system: problems first (failed outcomes, stuck hides), then how recently it reported. */
export function healthStatus(h: HealthLike, now: number = Date.now()): { tone: 'green' | 'amber' | 'red'; text: string } {
  if (h.stuckPending > 0) return { tone: 'red', text: `${h.stuckPending} hide(s) stuck pending` };
  if (h.failed24h > 0) return { tone: 'red', text: `${h.failed24h} failed action(s) in 24 h` };
  if (!h.lastAt) return { tone: 'amber', text: 'no checks in 24 h' };
  const minutes = Math.round((now - Date.parse(h.lastAt)) / 60000);
  if (h.checks1h > 0) return { tone: 'green', text: `working — last check ${minutes} min ago` };
  return { tone: 'amber', text: `quiet — last check ${Math.round(minutes / 60)} h ago` };
}

/** How hub decided, in words (moderation_event.source + ruleId). */
export function sourceText(source: string, ruleId: string | null): string {
  if (source === 'rule') {
    const term = ruleId?.startsWith('abuse.lexicon:') ? ruleId.slice('abuse.lexicon:'.length) : null;
    return term ? `swear word "${term}"` : 'spam rule';
  }
  if (source === 'example') return 'similar example';
  return '—';
}

/** hub errors arrive as Nest JSON in Error.message (services throw `await res.text()`). */
export function apiError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const j = JSON.parse(raw) as { message?: unknown };
    if (Array.isArray(j.message)) return j.message.join('; ');
    if (typeof j.message === 'string') return j.message;
  } catch {
    /* not JSON */
  }
  return raw;
}

export function outcomeTone(outcome: string | null): 'gray' | 'amber' | 'green' | 'red' {
  if (outcome === 'hidden' || outcome === 'deleted' || outcome === 'failed') return 'red';
  if (outcome === 'flagged' || outcome === 'pending') return 'amber';
  if (outcome === 'undone') return 'green';
  return 'gray';
}
